// forms/html_element_helpers/error_popover.js

import { default_selectors } from '../../helpers/static_vars';

export const generate_random_error_popover_id = () =>
    `error-popover-${Math.random().toString(36).substring(2, 11)}`;

// Single, self-contained stylesheet for the error toast. ALL selectors are
// anchored to `.data-hub-error-popover-container` so they can never match host
// page elements. No generic utility classes, no `*` rules outside our subtree,
// no element selectors without our prefix — adding this <style> to document.head
// cannot affect host page layout or styling.
//
// Why we ship this in JS instead of relying on Tailwind: the toast is rendered
// in document.body (not the shadow root) so it can escape any transformed
// ancestor that would otherwise capture `position: fixed`. Outside the shadow
// root we don't have Tailwind, so we need self-contained CSS.
const ERROR_POPOVER_STYLES_ATTRIBUTE = 'data-hub-error-popover-styles';
const ERROR_POPOVER_STYLES_CSS = `
.data-hub-error-popover-container {
    position: fixed;
    top: 1rem;
    right: 1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    padding: 1rem;
    margin: 0;
    pointer-events: none;
    z-index: 2147483647;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #b91c1c;
}
.data-hub-error-popover-container,
.data-hub-error-popover-container * {
    box-sizing: border-box;
}
.data-hub-error-popover-container .data-hub-error-popover {
    pointer-events: auto;
}
.data-hub-error-popover-container .data-hub-error-popover-card {
    display: flex;
    align-items: flex-start;
    width: 300px;
    max-width: 90vw;
    padding: 0.75rem 1rem;
    background-color: #fee2e2;
    border: 1px solid #f87171;
    border-radius: 0.25rem;
    color: #b91c1c;
}
.data-hub-error-popover-container .data-hub-error-popover-body {
    flex: 1 1 auto;
    min-width: 0;
}
.data-hub-error-popover-container .data-hub-error-popover-title {
    margin: 0 0 0.25rem 0;
    font-weight: 700;
    font-size: 0.875rem;
    line-height: 1.25;
}
.data-hub-error-popover-container .data-hub-error-popover-message {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.25;
    word-wrap: break-word;
}
.data-hub-error-popover-container .data-hub-error-popover-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.5rem;
    cursor: pointer;
    background: none;
    border: 0;
    padding: 0;
}
.data-hub-error-popover-container .data-hub-error-popover-close-icon {
    width: 1.5rem;
    height: 1.5rem;
    display: block;
}
`;

/**
 * Inject our toast stylesheet into document.head exactly once per page. Idempotent —
 * second call is a no-op via the data attribute marker.
 */
const ensure_error_popover_styles_injected = () => {
    if (typeof document === 'undefined' || !document.head) {
        return;
    }
    if (document.head.querySelector(`style[${ERROR_POPOVER_STYLES_ATTRIBUTE}]`)) {
        return;
    }
    const style = document.createElement('style');
    style.setAttribute(ERROR_POPOVER_STYLES_ATTRIBUTE, '');
    style.type = 'text/css';
    style.textContent = ERROR_POPOVER_STYLES_CSS;
    document.head.appendChild(style);
};

/**
 * Creates an error popover container to hold error notifications. Always anchors
 * the container in document.body (regardless of the `parent` argument, which is
 * kept for backward compatibility but ignored). Reason: the toast is
 * `position: fixed` and becomes relative to the nearest *transformed* ancestor,
 * not the viewport — when rendered inside a shadow DOM whose host page (or any
 * ancestor of the host) has `transform`, `filter`, `perspective`, or
 * `will-change`, the toast paints off-screen. Mounting in document.body sidesteps
 * the whole class of containing-block issues.
 *
 * @param {HTMLElement} _parent - Ignored; kept for API compatibility.
 * @param {String} error_container_selector - The selector to find the container.
 * @returns {HTMLElement} The error popover container element.
 */
export const createErrorPopoverContainer = (
    _parent = document.body,
    error_container_selector = default_selectors.error_container_selector
) => {
    ensure_error_popover_styles_injected();
    const body = typeof document !== 'undefined' ? document.body : null;
    if (!body) {
        return null;
    }
    let container = body.querySelector(error_container_selector);
    if (!container) {
        container = document.createElement('div');
        // Single class — all positioning, layout, and z-index come from the
        // self-contained stylesheet above. No utility classes.
        container.className = 'data-hub-error-popover-container';
        body.appendChild(container);
    }
    return container;
};

/**
 * Close an error popover.
 * @param {Event} event - The event that triggered the close.
 * @param {HTMLElement} this_element - The element to close.
 */
export const close_error_popover = (event = null, this_element = null) => {
    if (event) {
        event.preventDefault();
        // Find the closest error popover if the event target is a child element
        if (!this_element) {
            this_element = event.target.closest(default_selectors.error_popover_selector);
        }
    }
    
    if (this_element) {

        // Add fade-out animation
        this_element.style.opacity = '0';
        this_element.style.transform = 'translateX(20px)';
        
        // Remove after animation completes
        setTimeout(() => {
            this_element.remove();
        }, 300);
    } else {
        // Find the nearest element with the error_popover class
        const error_popover = event.target.closest(default_selectors.error_popover_selector);
        if (error_popover) {
            close_error_popover(null, error_popover);
        }

    }
};

export const find_or_create_error_popover_container = (
    error_container_selector = default_selectors.error_container_selector,
    /* eslint-disable-next-line no-unused-vars */
    parent_container = document
) => {
    // parent_container is ignored — toast lives in document.body now. See
    // createErrorPopoverContainer for the why. Param kept for API compatibility.
    if (typeof document === 'undefined' || !document.body) {
        return null;
    }
    let error_popover_container = null;
    try {
        if (error_container_selector instanceof HTMLElement) {
            error_popover_container = error_container_selector;
        } else if (typeof error_container_selector === 'string') {
            if (error_container_selector.startsWith('#')) {
                error_popover_container = document.getElementById(error_container_selector.slice(1));
            } else {
                error_popover_container = document.body.querySelector(error_container_selector);
            }
        }
    } catch (error) {
        window?.data_hub?.error_function?.('Error finding return error container: ' + error, null);
    }

    if (!error_popover_container) {
        error_popover_container = createErrorPopoverContainer(document.body, error_container_selector);
    }

    return error_popover_container;
};

/**
 * Trigger a Toast-like error notification
 * @param {String} external_error_message to display to the user
 * @param {HTMLElement} parent_container of the target element to append the error popover
 * @param {String, HTMLElement, tagName} error_container_selector to find or create the error container defaults to '.data-hub-error-popover-container'
 * @returns {HTMLElement} error_popover
 */
export const show_error_popover = (
    external_error_message = "Something went wrong please try again later.",
    parent_container = document,
    error_container_selector = default_selectors.error_container_selector
) => {

    if (!external_error_message || external_error_message === '' || typeof external_error_message !== 'string') {
        return;
    }

    // Ensure the container exists
    const container = find_or_create_error_popover_container(error_container_selector, parent_container);

    // If no container still return an error
    if (!container || !(container instanceof HTMLElement)) {
        window?.data_hub?.error_function?.('Error finding or creating error container: ' + error_container_selector, null);
        return alert(external_error_message || 'An error occurred.');
    }
    
    // Generate a unique ID for the new error popover
    const error_popover = generate_error_popover_div({
        parent_container: container,
        external_error_message: external_error_message,
        id: generate_random_error_popover_id()
    });
    
    return error_popover;
};

/**
 * Generate an error popover div
 * @param {HTMLElement} parent_container - The parent container to append the error popover to.
 * @param {String} id - The unique ID for the error popover.
 * @returns {HTMLElement} error_popover
 */
export const generate_error_popover_div = ({
    parent_container = null,
    external_error_message = "Something went wrong please try again later.",
    id = generate_random_error_popover_id()
} = {}) => {

    // Create the container if it doesn't exist
    if (!parent_container) {
        parent_container = find_or_create_error_popover_container();
    }

    // Markup uses only `.data-hub-error-popover-*` classes — every selector in
    // the injected stylesheet is anchored to .data-hub-error-popover-container,
    // so this DOM subtree is fully self-contained and the host page's CSS will
    // not affect it (and our CSS will not affect host page elements).
    const error_popover_div = document.createElement('div');
    error_popover_div.innerHTML = `
  <div id="${id}" class="data-hub-error-popover">
    <div class="data-hub-error-popover-card" role="alert">
      <div class="data-hub-error-popover-body">
        <p class="data-hub-error-popover-title">Error!</p>
        <p class="data-hub-error-popover-message data_hub_error_message">${external_error_message}</p>
      </div>
      <span class="data-hub-error-popover-close" aria-label="Close error popover">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
             fill="none" stroke="#ef4444" stroke-width="1.5"
             class="data-hub-error-popover-close-icon">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </span>
    </div>
  </div>
  `;

    // Initial state for animation
    error_popover_div.style.opacity = '0';
    error_popover_div.style.transform = 'translateX(-20px)';
    error_popover_div.style.transition = 'opacity 300ms ease-in-out, transform 300ms ease-in-out';
    
    parent_container.appendChild(error_popover_div);

    // Attach click event to close the popover
    error_popover_div.addEventListener('click', (event) => close_error_popover(event, error_popover_div));

    // Animate in
    setTimeout(() => {
        error_popover_div.style.opacity = '1';
        error_popover_div.style.transform = 'translateX(0)';
    }, 10);

    // Auto-close after timeout; use parent_container.querySelector to check existence
    setTimeout(() => {
        close_error_popover(null, error_popover_div);
    }, 5000);

    return error_popover_div;
};
